import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Connexion réussie");
      nav(loc.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Se connecter"
      subtitle="Accédez à votre espace pro."
      footer={
        <>
          Pas encore client ?{" "}
          <Link
            to="/access-request"
            className="font-medium text-[#C4553B] hover:underline"
            data-testid="link-access-request"
          >
            Demander un accès
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-300">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border-slate-700 bg-slate-950/40 text-slate-100"
            data-testid="login-email-input"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <Label htmlFor="password" className="text-slate-300">
              Mot de passe
            </Label>
            <Link
              to="/forgot-password"
              className="text-xs text-[#C4553B] hover:underline"
              data-testid="link-forgot"
            >
              Oublié ?
            </Link>
          </div>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border-slate-700 bg-slate-950/40 text-slate-100"
            testIdPrefix="login-password"
          />
        </div>
        {error && (
          <div className="rounded border border-red-500/30 bg-red-500/10 p-2.5 text-sm text-red-300" data-testid="login-error">
            {error}
          </div>
        )}
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#C4553B] text-white hover:bg-[#A8402C]"
          data-testid="login-submit-btn"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Se connecter
        </Button>
      </form>
    </AuthShell>
  );
}
